import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2, X as XIcon } from 'lucide-react';
import {
  listIntegrations, createIntegration, updateIntegration, deleteIntegration,
} from 'features/evals/api/evalsApi';
import ProviderLogo from 'shared/components/ui/ProviderLogo';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { useTeam } from 'shared/context/teamContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableRowClass,
  tableDataCellClass,
  activityNeutralButtonClass,
} from 'features/activity/styles/dataTableStyles';
import {
  settingsSolidButtonClass,
  settingsIconButtonClass,
  settingsTableShellClass,
  settingsModalClass,
} from 'features/settings/components/settingsTheme';
import { BrandLoaderInline } from 'features/settings/components/settingsLoader';

/**
 * IntegrationsPanel — Model Configurations.
 *
 * Single source of truth for BYO model credentials used across the app:
 *   - LLM-judge evaluators (Evals)
 *   - AI Agent + LLM nodes in Agents/Workflows
 *   - Any future feature that calls a model
 *
 * Plaintext keys / service-account JSON are sent over HTTPS, encrypted with
 * pgcrypto on the server, and never returned. The UI only ever shows last-4
 * chars of the key (`key_prefix`).
 *
 * Providers across four families:
 *   Direct API:    openai, anthropic, gemini, mistral, fireworks, groq,
 *                  deepseek, xai
 *   Generic:       openai_compat  (custom OpenAI-compatible endpoint)
 *   Cloud:         azure_openai   (azure_endpoint + api_version + deployment)
 *                  vertex_ai      (project + location + service-account JSON)
 *                  bedrock        (aws_region + AK/SK)
 *   Decision:      jev, trodo     (answer typed questions, never chat; a
 *                                  default model + optional https base URL)
 */

const PROVIDER_GROUPS = [
  {
    label: 'Direct providers',
    options: [
      { value: 'openai',     label: 'OpenAI',     placeholder: 'sk-...' },
      { value: 'anthropic',  label: 'Anthropic',  placeholder: 'sk-ant-...' },
      { value: 'gemini',     label: 'Gemini',     placeholder: 'AIza...' },
      { value: 'mistral',    label: 'Mistral AI' },
      { value: 'fireworks',  label: 'Fireworks AI' },
      { value: 'groq',       label: 'Groq',       placeholder: 'gsk_...' },
      { value: 'deepseek',   label: 'DeepSeek' },
      { value: 'xai',        label: 'xAI',        placeholder: 'xai-...' },
    ],
  },
  {
    label: 'OpenAI-compatible',
    options: [
      { value: 'openai_compat', label: 'OpenAI-compatible endpoint' },
    ],
  },
  {
    label: 'Cloud providers',
    options: [
      { value: 'azure_openai', label: 'Azure OpenAI' },
      { value: 'vertex_ai',    label: 'Google Vertex AI' },
      { value: 'bedrock',      label: 'Amazon Bedrock' },
    ],
  },
  {
    label: 'Decision models',
    options: [
      { value: 'jev',   label: 'Jev' },
      { value: 'trodo', label: 'Hartley (Trodo Console)', placeholder: 'trodo_sk_...' },
    ],
  },
];

// Decision models answer typed questions about a state; they never chat. The
// list asks for kind=all so they show here, tagged, and nowhere a chat model
// is picked.
const DECISION_PROVIDERS = new Set(['jev', 'trodo']);
const isDecisionRow = (r) => r?.kind === 'decision' || DECISION_PROVIDERS.has(r?.provider);

const ALL_PROVIDERS = PROVIDER_GROUPS.flatMap(g => g.options);
const PROVIDER_LABEL = Object.fromEntries(ALL_PROVIDERS.map(o => [o.value, o.label]));

// Models now come from the synced catalog (chosen at use time), so the user no
// longer types a model list when adding a key. The two exceptions have no
// catalog: Azure "models" are per-resource DEPLOYMENT names, and openai_compat
// points at an arbitrary endpoint — both still need a manual list.
const MANUAL_MODEL_PROVIDERS = new Set(['azure_openai', 'openai_compat']);
const needsManualModels = (provider) => MANUAL_MODEL_PROVIDERS.has(provider);

// Mirror of the backend normalizer (services/models/azureEndpoint.js) for a live
// "here's what we'll save" preview. The backend re-normalizes on save and is the
// source of truth — keep these two in sync if either changes.
function normalizeAzureUri(input) {
  const raw = String(input || '').trim();
  if (!raw) return { base_url: '', deployment: null };
  let url;
  try { url = new URL(raw.includes('://') ? raw : `https://${raw}`); }
  catch (_) { return { base_url: raw.replace(/\/+$/, ''), deployment: null }; }
  let deployment = null;
  const m = url.pathname.match(/\/deployments\/([^/]+)/i);
  if (m) deployment = decodeURIComponent(m[1]);
  let host = url.hostname;
  if (/\.cognitiveservices\.azure\.com$/i.test(host)) host = `${host.split('.')[0]}.services.ai.azure.com`;
  return { base_url: `https://${host}/openai/v1`, deployment };
}

// Per-provider config-field declarations the form renders + serializes.
// Each credential now ALSO stores a multi-model list in config.models[],
// so users can add every model they intend to use at workflow time
// (e.g. gemini-2.5-pro, gemini-2.5-flash) and pick from a dropdown later.
const CONFIG_FIELDS = {
  openai_compat: [
    { key: 'base_url', label: 'Base URL', placeholder: 'https://api.together.xyz/v1', required: true },
  ],
  azure_openai: [
    { key: 'endpoint', label: 'Target URI (paste from Foundry)', placeholder: 'https://<resource>.services.ai.azure.com/models/…  or  …/openai/deployments/…', required: true },
  ],
  vertex_ai: [
    { key: 'project_id', label: 'GCP project id', placeholder: 'my-gcp-project', required: true },
    { key: 'location',   label: 'Location',       placeholder: 'us-central1',    required: true },
    { key: 'base_url',   label: 'Base URL',       placeholder: 'https://us-central1-aiplatform.googleapis.com', required: true },
  ],
  bedrock: [
    { key: 'aws_access_key_id', label: 'AWS access key id', placeholder: 'AKIA...', required: true },
    { key: 'aws_region',        label: 'AWS region',        placeholder: 'us-east-1', required: true },
    { key: 'endpoint_url',      label: 'Endpoint URL',      placeholder: 'https://bedrock-runtime.us-east-1.amazonaws.com', required: true },
    { key: 'inference_profile', label: 'Inference profile', placeholder: 'us. (cross-region) or default', required: true },
  ],
};

// Provider-specific labels for the models multi-input. Azure uses
// "Deployment names" because deployments ARE models in Azure.
const MODELS_LABEL = {
  azure_openai: 'Deployment names',
  default:      'Models',
};

const MODELS_PLACEHOLDER = {
  openai:        'gpt-4o, gpt-4o-mini, o1 …',
  anthropic:     'claude-haiku-4-5-20251001 …',
  gemini:        'gemini-2.5-pro, gemini-2.5-flash …',
  mistral:       'mistral-large-latest …',
  fireworks:     'accounts/fireworks/models/llama-v3p1-70b-instruct …',
  groq:          'llama-3.3-70b-versatile …',
  deepseek:      'deepseek-chat, deepseek-reasoner …',
  xai:           'grok-4, grok-3-mini …',
  openai_compat: 'model-name-1, model-name-2 …',
  azure_openai:  'gpt-4o-deployment …',
  vertex_ai:     'gemini-2.5-pro …',
  bedrock:       'anthropic.claude-3-5-sonnet-20241022-v2:0 …',
};

// A few well-known model ids users may want to add with one click.
const MODELS_SUGGESTIONS = {
  openai:        ['gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini', 'gpt-4-turbo'],
  anthropic:     ['claude-haiku-4-5-20251001', 'claude-sonnet-4-5', 'claude-opus-4-5-20251001'],
  gemini:        ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-1.5-pro'],
  mistral:       ['mistral-large-latest', 'mistral-small-latest', 'codestral-latest'],
  fireworks:     ['accounts/fireworks/models/llama-v3p1-70b-instruct', 'accounts/fireworks/models/qwen3-coder-480b-a35b-instruct'],
  groq:          ['llama-3.3-70b-versatile', 'mixtral-8x7b-32768', 'llama-3.1-8b-instant'],
  deepseek:      ['deepseek-chat', 'deepseek-reasoner'],
  xai:           ['grok-4', 'grok-3-mini', 'grok-2-vision'],
  vertex_ai:     ['gemini-2.5-pro', 'gemini-2.5-flash'],
  bedrock:       ['anthropic.claude-3-5-sonnet-20241022-v2:0', 'anthropic.claude-3-5-haiku-20241022-v1:0', 'meta.llama3-1-70b-instruct-v1:0'],
};

const KEY_LABEL = {
  bedrock:   'AWS secret access key',
  jev:       'TypeSafe API key',
  trodo:     'Trodo Console API key',
  default:   'API key',
};

/**
 * Settings → Project → Models.
 *
 * BYO model provider keys for Evals and Agents. Moved here from the
 * Integrations page's "Model Configurations" card.
 *
 * Access: anyone with team access can VIEW the configured providers (the list
 * shows only a key prefix, never the secret). Adding, editing and removing is
 * owner/admin/developer via INTEGRATIONS_DEVELOPER_MANAGE — enforced on the
 * server in routes/evalsRouter.js; the gating here just keeps a member from
 * filling in a form that would 403 on submit.
 */
export default function ModelsPanel() {
  const { selectedTeam } = useTeam();
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);
  const teamId = selectedTeam?.id || selectedTeam?._id || null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const dialog = useAppDialog();

  async function refresh() {
    if (!teamId) return;
    setLoading(true);
    try {
      setRows(await listIntegrations(teamId, { kind: 'all' }));
      setError(null);
    } catch (e) { setError(humanError(e)); }
    finally    { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [teamId]);

  // The one action sits in the top bar, in line with the page title.
  useBreadcrumbActions(
    canManage ? (
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        className="inline-flex h-6 items-center gap-1 rounded-md bg-brand px-2.5 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover"
      >
        Add model
      </button>
    ) : null,
  );

  async function onDelete(id) {
    const ok = await dialog.confirm('Anything using this model (workflows, evaluators) will start erroring once it is removed.', {
      title: 'Delete this model configuration?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      danger: true,
    });
    if (!ok) return;
    try { await deleteIntegration(teamId, id); await refresh(); }
    catch (e) { setError(humanError(e)); }
  }

  return (
    <div className="space-y-3">
      {!canManage && (
        <p className="text-[12px] text-[color:var(--text-muted)]">Only owners, admins and developers can change these.</p>
      )}

      {error && <p role="alert" className="text-[12px] text-[color:var(--feedback-error)]">{error}</p>}

      {loading && rows.length === 0 ? (
        <BrandLoaderInline label="Loading models" />
      ) : (
      <div className={settingsTableShellClass}>
        <div className="overflow-x-auto">
        <table className="w-full text-left">
          <caption className="sr-only">Configured model providers</caption>
          <thead>
            <tr className={tableHeaderRowClass}>
              <th scope="col" className={tableHeaderCellClass}>Provider</th>
              <th className={tableHeaderCellClass}>Label</th>
              <th className={tableHeaderCellClass}>Models</th>
              <th className={tableHeaderCellClass}>Key</th>
              <th className={tableHeaderCellClass}>Added</th>
              {canManage && <th scope="col" className="h-8 px-3 w-1 whitespace-nowrap"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="px-3.5 py-8 text-center text-[12px] text-[color:var(--text-muted)]">
                  No models configured
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const models = Array.isArray(r.config?.models) ? r.config.models : [];
              return (
                <tr key={r.id} className={`${tableRowClass.replace('cursor-pointer ', '')} group`}>
                  <td className={tableDataCellClass}>
                    <span className="inline-flex items-center gap-2">
                      <ProviderLogo provider={r.provider} size={16} />
                      {PROVIDER_LABEL[r.provider] || r.provider}
                      {isDecisionRow(r) && (
                        <span className="rounded border border-[color:var(--border-default)] px-1 text-[10px] leading-4 text-[color:var(--text-muted)]">Decision</span>
                      )}
                    </span>
                  </td>
                  <td className={tableDataCellClass}>{r.label}</td>
                  <td className={`${tableDataCellClass} text-[color:var(--text-muted)] max-w-[220px] truncate`}>
                    {!needsManualModels(r.provider)
                      ? <span className="text-[11px]">Catalog</span>
                      : models.length === 0
                        ? <span className="text-[11px]" style={{ color: 'var(--feedback-error)' }}>None</span>
                        : models.length <= 2
                          ? models.join(', ')
                          : `${models[0]}, ${models[1]} +${models.length - 2}`}
                  </td>
                  <td className={`${tableDataCellClass} font-mono text-[11px] text-[color:var(--text-muted)]`}>{r.key_prefix || '—'}</td>
                  <td className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>{new Date(r.created_at).toLocaleDateString()}</td>
                  {canManage && (
                    <td className="px-3 text-right whitespace-nowrap w-1">
                      <div className="inline-flex gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                        {needsManualModels(r.provider) && (
                          <button type="button" title="Edit" className={settingsIconButtonClass}
                            onClick={() => setEditing(r)}
                            aria-label={`Edit ${r.label} models`}
                          ><Pencil size={13} aria-hidden /></button>
                        )}
                        <button type="button" title="Delete" className={settingsIconButtonClass}
                          onClick={() => onDelete(r.id)}
                          aria-label={`Delete ${r.label}`}
                        ><Trash2 size={13} aria-hidden /></button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      )}

      {showAdd && (
        <AddIntegrationModal
          teamId={teamId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}

      {editing && (
        <EditModelsModal
          teamId={teamId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}


function AddIntegrationModal({ teamId, onClose, onSaved }) {
  const [provider, setProvider] = useState('openai');
  const [label, setLabel] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [config, setConfig] = useState({});
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const meta = useMemo(() => ALL_PROVIDERS.find(o => o.value === provider) || {}, [provider]);
  const configFields = CONFIG_FIELDS[provider] || [];
  const keyLabel = KEY_LABEL[provider] || KEY_LABEL.default;
  const modelsLabel = MODELS_LABEL[provider] || MODELS_LABEL.default;
  const modelsPlaceholder = MODELS_PLACEHOLDER[provider] || 'Type and press Enter';
  const modelsSuggestions = MODELS_SUGGESTIONS[provider] || [];
  const isDecision = DECISION_PROVIDERS.has(provider);
  useEffect(() => { setConfig({}); setPlaintext(''); setModels([]); }, [provider]);

  // Azure: fold the deployment name embedded in the pasted Target URI into the
  // deployment list automatically (the /openai/deployments/<name>/ shape).
  useEffect(() => {
    if (provider !== 'azure_openai') return;
    const { deployment } = normalizeAzureUri(config.endpoint || '');
    if (deployment) setModels((prev) => (prev.includes(deployment) ? prev : [...prev, deployment]));
  }, [provider, config.endpoint]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!label) { setError('Label is required'); return; }
    if (!plaintext) { setError(`${keyLabel} is required`); return; }
    for (const f of configFields) {
      if (f.required && !config[f.key]) { setError(`${f.label} is required`); return; }
    }
    const manual = needsManualModels(provider);
    if (manual && models.length === 0) { setError(`At least one ${modelsLabel.toLowerCase().replace(/s$/, '')} is required`); return; }
    setSaving(true);
    try {
      await createIntegration(teamId, isDecision ? {
        provider, label,
        plaintext_key: plaintext,
        config: {},
      } : {
        provider, label,
        plaintext_key: plaintext,
        config: manual ? { ...config, models } : { ...config },
      });
      onSaved?.();
    } catch (e) { setError(humanError(e)); }
    finally { setSaving(false); }
  }

  const content = (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`${settingsModalClass} relative max-w-lg overflow-hidden`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <ProviderLogo provider={provider} size={20} />
            <span className="text-[14px] font-semibold text-[color:var(--text-primary)]">Add model</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Modal body */}
        <form onSubmit={submit} className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Provider">
              <ProviderDropdown value={provider} onChange={setProvider} />
            </Field>
            <Field label="Label">
              <input className="ip-input" placeholder="prod, eval, staging…" value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>

            {configFields.map((f) => (
              <Field key={f.key} label={f.label + (f.required ? ' *' : '')} className="md:col-span-2">
                <input
                  className="ip-input"
                  placeholder={f.placeholder}
                  value={config[f.key] || ''}
                  onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                />
              </Field>
            ))}

            <Field label={keyLabel + ' *'} className="md:col-span-2">
              <input
                type="password" autoComplete="off"
                className="ip-input font-mono"
                placeholder={meta.placeholder || '…'}
                value={plaintext} onChange={(e) => setPlaintext(e.target.value)}
              />
            </Field>

            {needsManualModels(provider) ? (
              <Field label={modelsLabel + ' *'} className="md:col-span-2">
                <TagInput
                  values={models}
                  onChange={setModels}
                  placeholder={modelsPlaceholder}
                  suggestions={modelsSuggestions}
                />
                <p className="mt-1.5 text-[11px] text-[color:var(--text-muted)]">Press Enter to add each.</p>
              </Field>
            ) : null}
          </div>

          {error && <p role="alert" className="mt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>}

          {/* Footer */}
          <div className="mt-4 flex items-center justify-end gap-2 border-t border-[color:var(--border-default)] pt-4">
            <button type="button" onClick={onClose} className={activityNeutralButtonClass}>Cancel</button>
            <button type="submit" disabled={saving} className={settingsSolidButtonClass}>
              {saving ? 'Verifying…' : 'Save'}
            </button>
          </div>
        </form>

        <style>{`
          .ip-input {
            border: 1px solid var(--border-default);
            border-radius: 6px;
            padding: 6px 10px;
            font-size: 12px;
            background: var(--surface-base);
            width: 100%;
            color: var(--text-primary);
            font-family: inherit;
            transition: border-color 0.15s;
          }
          .ip-input:focus { outline: none; border-color: var(--text-primary); }
          .ip-input::placeholder { color: var(--text-disabled); }
        `}</style>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
}

const PROVIDER_OPTIONS = PROVIDER_GROUPS.map((g) => ({
  groupLabel: g.label,
  options: g.options.map((o) => ({ value: o.value, label: o.label, icon: <ProviderLogo provider={o.value} size={18} /> })),
}));

function ProviderDropdown({ value, onChange }) {
  return (
    <BrandDropdown
      value={value}
      onChange={onChange}
      options={PROVIDER_OPTIONS}
      placeholder="Select provider"
      searchable
      searchPlaceholder="Search providers"
      maxHeight={360}
      ariaLabel="Provider"
    />
  );
}

/**
 * TagInput — chip-style multi-value input.
 * Type → press Enter (or comma) → value becomes a chip below.
 * Backspace on empty input deletes the last chip.
 */
function TagInput({ values = [], onChange, placeholder, suggestions = [] }) {
  const [draft, setDraft] = useState('');

  function commit(rawValue) {
    const v = (rawValue || draft).trim();
    if (!v) return;
    if (values.includes(v)) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  }
  function remove(idx) {
    const next = values.filter((_, i) => i !== idx);
    onChange(next);
  }
  function onKey(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !draft && values.length) {
      e.preventDefault();
      remove(values.length - 1);
    }
  }
  function onBlur() {
    if (draft.trim()) commit();
  }

  return (
    <div>
      <div
        className="flex flex-wrap gap-1.5 items-center"
        style={{
          border: '1px solid var(--border-default)', borderRadius: 6,
          padding: '6px 8px', background: 'var(--surface-base)', minHeight: 38,
        }}
      >
        {values.map((v, idx) => (
          <span
            key={`${v}-${idx}`}
            className="inline-flex items-center gap-1 font-mono"
            style={{
              background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 6,
              padding: '2px 6px 2px 8px', fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.4,
            }}
          >
            {v}
            <button type="button" onClick={() => remove(idx)}
              className="p-0.5 rounded hover:bg-surface-hover text-[color:var(--text-muted)] hover:text-[color:var(--feedback-error)]"
              aria-label={`Remove ${v}`}
            >
              <XIcon style={{ width: 11, height: 11 }} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={onBlur}
          placeholder={values.length === 0 ? placeholder : ''}
          className="font-mono"
          style={{
            flex: 1, minWidth: 120, outline: 'none', border: 'none',
            background: 'transparent', fontSize: 13, color: 'var(--text-primary)',
          }}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          
          {suggestions.filter((s) => !values.includes(s)).slice(0, 6).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => commit(s)}
              className="font-mono"
              style={{
                fontSize: 11, padding: '2px 6px', borderRadius: 5,
                border: '1px solid var(--border-default)', color: 'var(--text-muted)', background: 'var(--surface-base)',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <div className="mb-1 text-[12px] text-[color:var(--text-muted)]">{label}</div>
      {children}
    </label>
  );
}

function humanError(e) {
  return e?.response?.data?.error || e?.message || 'Request failed';
}

/** Small modal: edit just the models list for an existing credential. */
function EditModelsModal({ teamId, row, onClose, onSaved }) {
  const provider = row.provider;
  const modelsLabel = MODELS_LABEL[provider] || MODELS_LABEL.default;
  const placeholder = MODELS_PLACEHOLDER[provider] || 'Type and press Enter';
  const suggestions = MODELS_SUGGESTIONS[provider] || [];
  const [models, setModels] = useState(row.config?.models || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setError(null);
    if (models.length === 0) { setError(`At least one ${modelsLabel.toLowerCase().replace(/s$/, '')} is required`); return; }
    setSaving(true);
    try {
      await updateIntegration(teamId, row.id, { config: { models } });
      onSaved?.();
    } catch (e) { setError(humanError(e)); }
    finally    { setSaving(false); }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      style={{ background: 'var(--surface-overlay)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`${settingsModalClass} max-w-md p-5`}
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <ProviderLogo provider={provider} size={18} />
          <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">
            {PROVIDER_LABEL[provider] || provider} · {row.label}
          </div>
        </div>
        <label className="block text-[12px] text-[color:var(--text-muted)] mb-1">
          {modelsLabel}
        </label>
        <TagInput
          values={models}
          onChange={setModels}
          placeholder={placeholder}
          suggestions={suggestions}
        />
        {error && (
          <div className="mt-3 text-[11px]" style={{ color: 'var(--feedback-error)' }} role="alert">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className={activityNeutralButtonClass}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={settingsSolidButtonClass}>
            {saving ? 'Verifying…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
